import { useEffect, useRef, useState } from "react";
import { Eye, Loader2, LogOut } from "lucide-react";
import { endViewAsBeacon, exitViewAs, fetchViewAsStatus } from "@/api/adminCenter";
import { clearViewAsState, getViewAsState } from "@/lib/viewAs";

/**
 * D-044 S5 (new-tab) — global STICKY view-as banner (AC-084) у view-as ВКЛАДЦІ.
 * Рендериться на root-layout НАД ProtectedRoute (App.tsx), тож «Вийти» завжди
 * доступний, навіть якщо контент не завантажився. Показує target / READ-ONLY /
 * countdown / «Вийти»; raw sessionId/targetUserId приховані (MP8).
 *
 * Вихід (3 рівні):
 *  (а) «Вийти» → exitViewAs(audit end) → clear → window.close() (вкладку
 *      відкрито скриптом) → fallback ended-екран якщо браузер не закрив.
 *  (б) Закриття вкладки → beforeunload best-effort end (keepalive fetch).
 *  (в) Гарантія: cron hard-expiry 15 хв (ViewAsSessionExpiryJob).
 * Countdown→0 / 30с-poll виявляє manual/expired/replaced → ended-екран.
 *
 * Адмінська вкладка bearer не має → банер там НЕ рендериться (getViewAsState
 * читає sessionStorage цієї вкладки).
 */

function secondsLeft(expiresAt: string): number {
  const t = new Date(expiresAt.replace(" ", "T")).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((t - Date.now()) / 1000));
}

export function ViewAsBanner() {
  const state = getViewAsState();
  const [left, setLeft] = useState(() => (state ? secondsLeft(state.expiresAt) : 0));
  const [ending, setEnding] = useState(false);
  const [endNotice, setEndNotice] = useState<string | null>(null);
  const checkedRef = useRef(false);

  // (б) закриття вкладки → best-effort end (keepalive переживає unload)
  useEffect(() => {
    if (!state) return;
    const onLeave = () => endViewAsBeacon(state.sessionId, state.secret);
    window.addEventListener("pagehide", onLeave);
    return () => window.removeEventListener("pagehide", onLeave);
  }, [state?.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // countdown tick
  useEffect(() => {
    if (!state) return;
    const id = window.setInterval(() => setLeft(secondsLeft(state.expiresAt)), 1000);
    return () => window.clearInterval(id);
  }, [state?.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // status poll (30с) + одразу при mount
  useEffect(() => {
    if (!state) return;
    let cancelled = false;

    const finish = (msg: string) => {
      clearViewAsState();
      setEndNotice(msg);
      // вкладку відкрито скриптом → закриваємо; якщо браузер блокує — лишається ended-екран
      window.setTimeout(() => window.close(), 1500);
    };

    const check = async () => {
      try {
        const s = await fetchViewAsStatus(state.sessionId, state.secret);
        if (cancelled) return;
        if (!s.active) {
          finish(
            s.endReason === "expired"
              ? "Сесію перегляду завершено автоматично (15 хв). Запис у журналі аудиту. Вкладку можна закрити."
              : "Сесію перегляду завершено. Вкладку можна закрити.",
          );
        }
      } catch {
        if (!cancelled) finish("Сесію перегляду завершено. Вкладку можна закрити.");
      }
    };

    if (!checkedRef.current) {
      checkedRef.current = true;
      void check();
    }
    const id = window.setInterval(check, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [state?.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // countdown → 0 → негайний server-check (lazy-expiry + audit)
  useEffect(() => {
    if (!state || left > 0 || endNotice) return;
    void fetchViewAsStatus(state.sessionId, state.secret)
      .then((s) => {
        if (!s.active) {
          clearViewAsState();
          setEndNotice("Сесію перегляду завершено автоматично (15 хв). Запис у журналі аудиту. Вкладку можна закрити.");
          window.setTimeout(() => window.close(), 1500);
        }
      })
      .catch(() => undefined);
  }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state && !endNotice) return null;

  if (endNotice) {
    return (
      <div role="status" className="flex shrink-0 items-center gap-2 border-b bg-warning px-4 py-2.5 text-sm text-warning-foreground">
        <Eye className="h-4 w-4 shrink-0" />
        <span className="flex-1">{endNotice}</span>
        <button
          type="button"
          onClick={() => window.close()}
          className="shrink-0 rounded-md border border-warning-foreground/40 bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent"
        >
          Закрити вкладку
        </button>
      </div>
    );
  }

  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");

  return (
    <div
      role="status"
      className="flex shrink-0 items-center gap-2 border-b border-warning-foreground/30 bg-warning px-4 py-2.5 text-sm text-warning-foreground"
    >
      <Eye className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        Ви переглядаєте як <b>{state!.targetName}</b> · режим <b>READ-ONLY</b> · спливає{" "}
        <span className="tabular-nums">{mm}:{ss}</span>
      </span>
      <button
        type="button"
        disabled={ending}
        onClick={async () => {
          setEnding(true);
          try {
            await exitViewAs(state!.sessionId, state!.secret);
          } catch {
            // сесія могла вже завершитись server-side — все одно чистимо
          }
          clearViewAsState();
          setEndNotice("Сесію перегляду завершено. Вкладку можна закрити.");
          window.setTimeout(() => window.close(), 800);
        }}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-warning-foreground/40 bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent"
      >
        {ending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
        Вийти
      </button>
    </div>
  );
}
